"use client"

import * as React from "react"
import { Command as CommandPrimitive } from "cmdk"
import { SearchIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

function Command({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive>) {
  return (
    <CommandPrimitive
      data-slot="command"
      className={cn(
        "cy:bg-popover cy:text-popover-foreground cy:flex cy:h-full cy:w-full cy:flex-col cy:overflow-hidden cy:rounded-md",
        className
      )}
      {...props}
    />
  )
}

function CommandDialog({
  title = "Command Palette",
  description = "Search for a command to run...",
  children,
  className,
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof Dialog> & {
  title?: string
  description?: string
  className?: string
  showCloseButton?: boolean
}) {
  return (
    <Dialog {...props}>
      <DialogHeader className="cy:sr-only">
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <DialogContent
        className={cn("cy:overflow-hidden cy:p-0", className)}
        showCloseButton={showCloseButton}
      >
        <Command className="cy:[&_[cmdk-group-heading]]:text-muted-foreground cy:**:data-[slot=command-input-wrapper]:h-12 cy:[&_[cmdk-group-heading]]:px-2 cy:[&_[cmdk-group-heading]]:font-medium cy:[&_[cmdk-group]]:px-2 cy:[&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 cy:[&_[cmdk-input-wrapper]_svg]:h-5 cy:[&_[cmdk-input-wrapper]_svg]:w-5 cy:[&_[cmdk-input]]:h-12 cy:[&_[cmdk-item]]:px-2 cy:[&_[cmdk-item]]:py-3 cy:[&_[cmdk-item]_svg]:h-5 cy:[&_[cmdk-item]_svg]:w-5">
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  )
}

function CommandInput({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Input>) {
  return (
    <div
      data-slot="command-input-wrapper"
      className="cy:flex cy:h-9 cy:items-center cy:gap-2 cy:border-b cy:px-3"
    >
      <SearchIcon className="cy:size-4 cy:shrink-0 cy:opacity-50" />
      <CommandPrimitive.Input
        data-slot="command-input"
        className={cn(
          "cy:placeholder:text-muted-foreground cy:flex cy:h-10 cy:w-full cy:rounded-md cy:bg-transparent cy:py-3 cy:text-sm cy:outline-hidden cy:disabled:cursor-not-allowed cy:disabled:opacity-50",
          className
        )}
        {...props}
      />
    </div>
  )
}

function CommandList({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      data-slot="command-list"
      className={cn(
        "cy:max-h-[300px] cy:scroll-py-1 cy:overflow-x-hidden cy:overflow-y-auto",
        className
      )}
      {...props}
    />
  )
}

function CommandEmpty({
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      data-slot="command-empty"
      className="cy:py-6 cy:text-center cy:text-sm"
      {...props}
    />
  )
}

function CommandGroup({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={cn(
        "cy:text-foreground cy:[&_[cmdk-group-heading]]:text-muted-foreground cy:overflow-hidden cy:p-1 cy:[&_[cmdk-group-heading]]:px-2 cy:[&_[cmdk-group-heading]]:py-1.5 cy:[&_[cmdk-group-heading]]:text-xs cy:[&_[cmdk-group-heading]]:font-medium",
        className
      )}
      {...props}
    />
  )
}

function CommandSeparator({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      data-slot="command-separator"
      className={cn("cy:bg-border cy:-mx-1 cy:h-px", className)}
      {...props}
    />
  )
}

function CommandItem({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Item>) {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={cn(
        "cy:data-[selected=true]:bg-accent cy:data-[selected=true]:text-accent-foreground cy:[&_svg:not([class*=text-])]:text-muted-foreground cy:relative cy:flex cy:cursor-default cy:items-center cy:gap-2 cy:rounded-sm cy:px-2 cy:py-1.5 cy:text-sm cy:outline-hidden cy:select-none cy:data-[disabled=true]:pointer-events-none cy:data-[disabled=true]:opacity-50 cy:[&_svg]:pointer-events-none cy:[&_svg]:shrink-0 cy:[&_svg:not([class*=size-])]:size-4",
        className
      )}
      {...props}
    />
  )
}

function CommandShortcut({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="command-shortcut"
      className={cn(
        "cy:text-muted-foreground cy:ml-auto cy:text-xs cy:tracking-widest",
        className
      )}
      {...props}
    />
  )
}

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
}
